/** Cliente HTTP com refresh automático e fila de retry.
 *
 * Em um 401, a PRIMEIRA request dispara o refresh e as demais ficam na fila:
 * sem isso, o "sino da escola" (várias chamadas simultâneas expirando juntas)
 * dispararia N rotações concorrentes — e a rotação com detecção de reuso
 * derrubaria a família inteira de tokens (RN-15).
 */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

import { readStoredRefresh, useAuthStore } from '@/stores/authStore'
import type { ProblemDetail, TokenResponse } from '@/types/api'

export const API_PREFIX = '/api/v1'

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

export const api: AxiosInstance = axios.create({
  baseURL: API_PREFIX,
  headers: { 'Content-Type': 'application/json' },
})

let refreshing: Promise<string | null> | null = null

async function runRefresh(): Promise<string | null> {
  const { refreshToken, setTokens, clear } = useAuthStore.getState()
  // Reler DENTRO do lock: outra aba pode ter rotacionado desde o carregamento.
  const currentRefresh = readStoredRefresh(refreshToken)
  if (!currentRefresh) {
    clear()
    return null
  }
  try {
    const { data } = await axios.post<TokenResponse>(`${API_PREFIX}/auth/refresh`, {
      refresh_token: currentRefresh,
    }, { timeout: 15000 })
    if (readStoredRefresh(currentRefresh) !== currentRefresh) return null
    setTokens(data.access_token, data.refresh_token)
    return data.access_token
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      if (readStoredRefresh(currentRefresh) === currentRefresh) clear()
      return null
    }
    // Offline, timeout, 429 e 5xx não são evidência de sessão inválida.
    throw error
  }
}

/** Um refresh de cada vez; quem chegou durante ele aguarda o mesmo resultado. */
export function refreshOnce(): Promise<string | null> {
  refreshing ??= (async () => navigator.locks
    ? await navigator.locks.request('lumina:reader-refresh', runRefresh)
    : await runRefresh())().finally(() => {
    refreshing = null
  })
  return refreshing
}

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ProblemDetail>) => {
    const config = error.config as RetriableConfig | undefined
    const status = error.response?.status
    const isRefreshCall = config?.url?.includes('/auth/refresh')

    if (status !== 401 || !config || config._retried || isRefreshCall) {
      return Promise.reject(error)
    }

    config._retried = true
    const token = await refreshOnce()
    if (!token) {
      return Promise.reject(error)
    }
    config.headers.set('Authorization', `Bearer ${token}`)
    return api.request(config)
  },
)

/** Extrai o `code` do catálogo RFC 7807 — o que o UI usa para decidir a mensagem. */
export function problemCode(error: unknown): string | null {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    return error.response?.data?.code ?? null
  }
  return null
}

export function problemDetail(error: unknown): string | null {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    return error.response?.data?.detail ?? null
  }
  return null
}

/** Status HTTP do erro, quando houve resposta.
 *
 * Usado para distinguir "o backend ainda não tem este endpoint" (404) de
 * "falhou": o leitor esconde a feature em vez de errar a cada abertura.
 */
export function problemStatus(error: unknown): number | null {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null
  }
  return null
}
